/**
 * MachineRow — Desktop: tabela flex | Mobile: card empilhado
 */
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { route } from 'preact-router';
import { api } from '../../api/client';
import { showToast } from '../../components/Toast';
import { T } from '../../styles/tokens';
import { Avatar, Button, Badge, Icon, IconButton, Spinner } from '../../components/ui';
import { statusColor, statusLabel, planLabel } from '../../utils/status';
import type { Environment } from './types';

interface MachineRowProps {
  env: Environment;
  userPlan?: string;
  isRestarting?: boolean;
  onRestart: () => void;
  onRefresh: () => void;
  onLogs: () => void;
  onDelete: () => void;
}

function MetricCell({ value }: { value?: number }) {
  if (value == null) return <span style={{ fontSize: 11, color: T.textMuted || T.textSecondary }}>—</span>;
  const color = value >= 90 ? T.danger : value >= 70 ? T.warning : T.textSecondary;
  return <span style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 600, color }}>{value}%</span>;
}

function MetricChip({ label, value }: { label: string; value?: number }) {
  if (value == null) return null;
  const color = value >= 90 ? T.danger : value >= 70 ? T.warning : T.textSecondary;
  return (
    <span style={{ fontSize: 11, color }}>
      <span style={{ color: T.textMuted, fontWeight: 400 }}>{label}</span>{' '}
      <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{value}%</span>
    </span>
  );
}

export function MachineRow({ env, userPlan, isRestarting, onRestart, onRefresh, onLogs, onDelete }: MachineRowProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const btnRef = useRef<HTMLDivElement>(null);

  const openMenu = useCallback(() => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const menuHeight = 280;
      const top = spaceBelow > menuHeight ? rect.bottom + 4 : rect.top - menuHeight - 4;
      const left = rect.right - 200;
      setMenuPos({ top, left: Math.max(8, left) });
    }
    setMenuOpen(v => !v);
  }, []);
  const menuRef = useRef<HTMLDivElement>(null);
  const isEnterprise = userPlan === 'enterprise';

  const sColor = isRestarting ? T.warning : statusColor(env);
  const sLabel = isRestarting ? 'Reiniciando' : statusLabel(env);
  const isCreating = ['creating', 'provisioning', 'configuring_setup', 'installing_openclaw', 'installing_llm'].includes(env.status);
  const isLocked = isCreating || !!isRestarting;
  const displayName = (env.name || '').replace(/\.(hstgr\.cloud|elon\.cloud)$/i, '');
  const metrics = env.metrics;
  const agentsList = env.agents && env.agents.length > 0 ? env.agents : env.agent_info ? [env.agent_info] : [];

  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      // Não fechar se clicou no botão ou dentro do dropdown (portal)
      if (menuRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const cpuVal = env.online && metrics ? (metrics.cpu_percent || 0) : undefined;
  const ramVal = env.online && metrics ? (metrics.memory_percent || 0) : undefined;
  const hdVal = env.online && metrics ? (metrics.disk_percent || 0) : undefined;

  // Menu dropdown compartilhado — renderiza via portal pra não ser cortado por overflow
  const DropdownMenu = ({ position }: { position: 'up' | 'down' }) => menuOpen ? createPortal(
    <div ref={dropdownRef} style={{
      position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999,
      background: '#1e1e1e', border: `1px solid ${T.border}`, borderRadius: 10,
      boxShadow: '0 8px 32px rgba(0,0,0,0.3)', minWidth: 200, padding: '4px 0',
    }}>
      <Button variant="ghost" disabled
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', fontSize: 12, background: 'none', border: 'none', opacity: 0.4, cursor: 'not-allowed' }}
      >
        <Icon name="settings" size={13} /> Configurar
      </Button>
      {env.status === 'running' && (
        <Button variant="ghost" onClick={() => {
          setMenuOpen(false);
          const wsId = (env.workspace_id || env.id || '').replace('ws-', '');
          api.post(`/cloud/workspaces/${wsId}/stop`, {}).then(() => onRestart()).catch(() => {});
        }}
          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', fontSize: 12, background: 'none', border: 'none' }}
        >⏸️ Pausar</Button>
      )}
      <Button variant="ghost" onClick={async () => {
        setMenuOpen(false);
        const wsId = (env.workspace_id || env.id || '').replace('ws-', '');
        const nome = prompt('Nome do backup: ', `Backup ${env.name} — ${new Date().toLocaleDateString('pt-BR')}`);
        if (!nome) return;
        try { await api.post(`/cloud/workspaces/${wsId}/backups`, { name: nome }, { timeout: 60000 } as any); showToast('Backup gerado!', 'success'); } catch (err: any) { showToast(err.message || 'Erro', 'error'); }
      }}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', fontSize: 12, background: 'none', border: 'none' }}
      >📸 Gerar Backup</Button>
      <Button variant="ghost" onClick={() => { setMenuOpen(false); const wsId = (env.workspace_id || env.id || '').replace('ws-', ''); window.dispatchEvent(new CustomEvent('open-backup-modal', { detail: { id: wsId, name: env.name } })); }}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', fontSize: 12, background: 'none', border: 'none' }}
      >🔄 Restaurar Backup</Button>
      <Button variant="ghost" onClick={async () => {
        setMenuOpen(false);
        const wsId = (env.workspace_id || env.id || '').replace('ws-', '');
        const nome = prompt('Nome do clone: ', `${env.name}-clone`);
        if (!nome) return;
        try { await api.post(`/cloud/workspaces/${wsId}/clone`, { name: nome }, { timeout: 60000 } as any); showToast('Clone criado!', 'success'); onRefresh(); } catch (err: any) { showToast(err.message || 'Erro', 'error'); }
      }}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', fontSize: 12, background: 'none', border: 'none' }}
      >📋 Clonar</Button>
      <div style={{ height: 1, background: T.border, margin: '4px 0' }} />
      <Button variant="ghost" onClick={async () => {
        setMenuOpen(false);
        const wsId = (env.workspace_id || env.id || '').replace(/^ws-/, '');
        if (!confirm(`Excluir "${env.name || wsId}"? Esta ação é irreversível.`)) return;
        try {
          await api.delete(`/cloud/workspaces/${wsId}`);
          showToast(`Ambiente ${env.name || wsId} excluído`, 'success');
          onRefresh();
        } catch (e: any) {
          showToast(e.message || 'Erro ao excluir', 'error');
        }
      }}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', fontSize: 12, fontWeight: 600, color: T.danger, background: 'none', border: 'none' }}
      ><Icon name="trash" size={13} /> Excluir</Button>
    </div>,
    document.body,
  ) : null;

  const ActionButtons = ({ menuPos }: { menuPos: 'up' | 'down' }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, opacity: isLocked ? 0.5 : 1, pointerEvents: isLocked ? 'none' : 'auto' }}>
      <Button variant="ghost" size="xs" onClick={onRestart}
        icon={<Icon name="refresh" spinning={!!isRestarting} size={13} />}
        style={{ borderRadius: 7, padding: '5px 8px', fontSize: 11 }}
      >{isRestarting ? '...' : 'Reiniciar'}</Button>
      <div ref={(el: any) => { menuRef.current = el; btnRef.current = el; }} style={{ position: 'relative' }}>
        <IconButton icon={<Icon name="more" size={14} />} onClick={openMenu} size={28} active={menuOpen} title="Mais opções" style={{ borderRadius: 7 }} />
        <DropdownMenu position={menuPos as any} />
      </div>
    </div>
  );

  const StatusBadge = () => (
    <span style={{
      padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
      background: `${sColor}18`, color: sColor,
      display: 'inline-flex', alignItems: 'center', gap: 4,
    }}>
      {isCreating && <Spinner size="sm" inline />}
      {sLabel}
    </span>
  );

  return (
    <>
      {/* ═══ DESKTOP ═══ */}
      <div class="machine-row-desktop" style={{
        display: 'flex', alignItems: 'center', gap: 0,
        padding: '12px 16px', borderBottom: `1px solid ${T.border}`,
        transition: 'background 0.1s', cursor: 'default',
      }}
        onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgCardHover || T.bgHover}
        onMouseLeave={(e: any) => e.currentTarget.style.background = 'transparent'}
      >
        <div style={{ width: 26, flexShrink: 0 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: sColor, display: 'inline-block', boxShadow: env.online ? `0 0 6px ${sColor}` : 'none' }} />
        </div>
        <div style={{ flex: 2, minWidth: 0, paddingRight: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
        </div>
        <div style={{ flex: 1.5, minWidth: 0, paddingRight: 12 }}>
          {agentsList.length > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              {agentsList.slice(0, 4).map((a, i) => (
                <div key={i} title={a.agent_name || a.name || '?'} style={{ marginLeft: i > 0 ? -6 : 0, zIndex: 4 - i, position: 'relative' }}>
                  <Avatar name={a.agent_name || a.name || '?'} size="sm" />
                </div>
              ))}
              {agentsList.length > 4 && <span style={{ fontSize: 10, color: T.textSecondary, marginLeft: 4 }}>+{agentsList.length - 4}</span>}
            </div>
          ) : <span style={{ fontSize: 11, color: T.textMuted || T.textSecondary }}>—</span>}
        </div>
        <div style={{ flex: 0.7, minWidth: 0, paddingRight: 12, textAlign: 'right' }}><MetricCell value={cpuVal} /></div>
        <div style={{ flex: 0.7, minWidth: 0, paddingRight: 12, textAlign: 'right' }}><MetricCell value={ramVal} /></div>
        <div style={{ flex: 0.7, minWidth: 0, paddingRight: 12, textAlign: 'right' }}><MetricCell value={hdVal} /></div>
        <div style={{ flex: 0.8, minWidth: 0, paddingRight: 12 }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: T.textSecondary, textTransform: 'uppercase' as const }}>{planLabel(env.plan_id, env.plan_name)}</span>
        </div>
        <div style={{ flex: 0.8, minWidth: 0, paddingRight: 12 }}><StatusBadge /></div>
        <div style={{ width: 110, justifyContent: 'flex-end' }}><ActionButtons menuPos="up" /></div>
      </div>

      {/* ═══ MOBILE ═══ */}
      <div class="machine-row-mobile" style={{ padding: '14px 16px', borderBottom: `1px solid ${T.border}` }}>
        {/* Topo: status + nome + ações */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: sColor, display: 'inline-block', boxShadow: env.online ? `0 0 6px ${sColor}` : 'none', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
              <StatusBadge />
              <span style={{ fontSize: 10, fontWeight: 600, color: T.textSecondary, textTransform: 'uppercase' as const }}>{planLabel(env.plan_id, env.plan_name)}</span>
            </div>
          </div>
          <ActionButtons menuPos="down" />
        </div>

        {/* Agentes + Métricas */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 10, paddingLeft: 19 }}>
          {agentsList.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              {agentsList.slice(0, 4).map((a, i) => (
                <div key={i} title={a.agent_name || a.name || '?'} style={{ marginLeft: i > 0 ? -6 : 0, zIndex: 4 - i, position: 'relative' }}>
                  <Avatar name={a.agent_name || a.name || '?'} size="sm" />
                </div>
              ))}
              {agentsList.length > 4 && <span style={{ fontSize: 10, color: T.textSecondary, marginLeft: 4 }}>+{agentsList.length - 4}</span>}
            </div>
          )}
          <MetricChip label="CPU" value={cpuVal} />
          <MetricChip label="RAM" value={ramVal} />
          <MetricChip label="HD" value={hdVal} />
        </div>
      </div>
    </>
  );
}

export const MachineCard = MachineRow;
